/**
 * Executive Force AI Navigation Component
 * 
 * Production-grade unified navigation component for the Executive Force AI ecosystem.
 * Provides consistent navigation experience across all applications.
 * 
 * Features:
 * - Dropdown menu to switch between applications
 * - Cross-app navigation with Admin-minted Firebase custom tokens
 * - Permission-based access control
 * - User menu with profile and settings
 * - Mobile responsive design
 * - Status badges for application availability
 * - Graceful error handling
 * 
 * Usage:
 * ```tsx
 * import BusinessSuiteNavigation from './BusinessSuiteNavigation';
 * 
 * <BusinessSuiteNavigation
 *   currentApp="financial-uk"
 *   user={currentUser}
 *   onLogout={handleLogout}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Chip,
  Avatar,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AccountBalance as FinanceIcon,
  Psychology as AIIcon,
  Inventory as SupplyChainIcon,
  People as CRMIcon,
  Assignment as ProjectIcon,
  ExpandMore as ExpandMoreIcon,
  ExitToApp as LogoutIcon,
  MonitorHeart as MonitoringIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountIcon,
  BusinessCenter as ExecutiveIcon,
} from '@mui/icons-material';

import {
  BUSINESS_APPLICATIONS,
  BusinessApplication,
  getAccessibleApplications,
  getApplicationById,
  getNavigationUrl,
} from './appRegistry';
import { buildSsoLaunchUrl, canMintSso } from '../../utils/suiteSso';

/**
 * Icon mapping for dynamic icon rendering
 */
const ICON_MAP: Record<string, React.ReactNode> = {
  Psychology: <AIIcon />,
  AccountBalance: <FinanceIcon />,
  BusinessCenter: <ExecutiveIcon />,
  People: <CRMIcon />,
  Inventory: <SupplyChainIcon />,
  Business: <BusinessIcon />,
  MonitorHeart: <MonitoringIcon />,
  Assignment: <ProjectIcon />,
};

/**
 * User interface
 */
export interface User {
  username: string;
  email: string;
  role: string;
  permissions: Record<string, string[]>;
}

/**
 * Component props
 */
export interface BusinessSuiteNavigationProps {
  currentApp?: string;
  user?: User;
  onLogout?: () => void;
  showUserMenu?: boolean;
  customActions?: React.ReactNode;
}

/**
 * Executive Force AI Navigation Component
 */
const BusinessSuiteNavigation: React.FC<BusinessSuiteNavigationProps> = ({
  currentApp = 'forceai',
  user,
  onLogout,
  showUserMenu = true,
  customActions,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [applications, setApplications] = useState<BusinessApplication[]>([]);

  /**
   * Initialize applications based on user permissions
   */
  useEffect(() => {
    if (user?.permissions) {
      const accessibleApps = getAccessibleApplications(user.permissions);
      setApplications(accessibleApps);
    } else {
      // If no user, show all active applications
      setApplications(BUSINESS_APPLICATIONS.filter(app => app.status === 'active'));
    }
  }, [user]);

  /**
   * Handle app menu open
   */
  const handleAppMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle app menu close
   */
  const handleAppMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handle user menu open
   */
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  /**
   * Handle user menu close
   */
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  /**
   * Handle application switch with authentication
   */
  const handleAppSwitch = async (app: BusinessApplication) => {
    if (app.status !== 'active') {
      console.warn(`Application ${app.name} is not active`);
      return;
    }

    try {
      const url = getNavigationUrl(app);
      
      // Navigate to application
      if (url.startsWith('/')) {
        // Relative path - use client-side routing if available
        window.location.href = url;
      } else if (url.includes(window.location.host)) {
        // Same origin - direct navigation
        window.location.href = url;
      } else if (app.id === 'portal' || !canMintSso(app.id)) {
        window.location.href = url;
      } else {
        window.location.href = await buildSsoLaunchUrl(url, app.id);
      }
    } catch (error) {
      console.error('Failed to switch application:', error);
      // Fallback to direct navigation without token
      window.location.href = app.frontendUrl;
    } finally {
      handleAppMenuClose();
    }
  };

  /**
   * Get current application data
   */
  const getCurrentApp = (): BusinessApplication => {
    return getApplicationById(currentApp) || BUSINESS_APPLICATIONS[0];
  };

  /**
   * Get status color for badges
   */
  const getStatusColor = (status: string): 'success' | 'primary' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'available':
        return 'primary';
      case 'coming_soon':
      default:
        return 'default';
    }
  };

  /**
   * Get status text for badges
   */
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'available':
        return 'Available';
      case 'coming_soon':
        return 'Coming Soon';
      default:
        return 'Unknown';
    }
  };

  /**
   * Get icon for application
   */
  const getIcon = (iconName: string): React.ReactNode => {
    return ICON_MAP[iconName] || <BusinessIcon />;
  };

  const currentAppData = getCurrentApp();

  return (
    <AppBar 
      position="static" 
      sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar
        variant="dense"
        disableGutters
        sx={{
          minHeight: '36px !important',
          maxHeight: '36px !important',
          height: '36px !important',
          px: 2,
          '@media (min-width:0px)': {
            minHeight: '36px !important',
          },
          '@media (min-width:600px)': {
            minHeight: '36px !important',
          },
        }}
      >
        {/* Executive Force AI Logo */}
        <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
        <Typography variant="body1" component="div" sx={{ mr: 2, fontWeight: 600, fontSize: '0.85rem' }}>
          Executive Force AI
        </Typography>

        {/* Current Application Selector */}
        <Tooltip title="Switch Application">
          <Button
            color="inherit"
            onClick={handleAppMenuOpen}
            endIcon={<ExpandMoreIcon />}
            sx={{
              mr: 2,
              textTransform: 'none',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              borderRadius: 1.5,
              px: 1.5,
              py: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              {getIcon(currentAppData.icon)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {currentAppData.name}
              </Typography>
            </Box>
            <Chip
              label={getStatusText(currentAppData.status)}
              size="small"
              color={getStatusColor(currentAppData.status)}
              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            />
          </Button>
        </Tooltip>

        {/* Application Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleAppMenuClose}
          PaperProps={{
            sx: { 
              minWidth: 350,
              maxHeight: 500,
              mt: 1,
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 600 }}>
            Available Applications
          </Typography>
          <Divider />
          {applications.map((app) => (
            <MenuItem
              key={app.id}
              onClick={() => handleAppSwitch(app)}
              disabled={app.status === 'coming_soon'}
              sx={{ 
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                }
              }}
            >
              <ListItemIcon>
                <Badge
                  color={getStatusColor(app.status)}
                  variant="dot"
                  invisible={app.status !== 'active'}
                >
                  {getIcon(app.icon)}
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={app.name}
                secondary={app.description}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
              <Chip
                label={getStatusText(app.status)}
                size="small"
                color={getStatusColor(app.status)}
                variant="outlined"
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Custom Actions */}
        {customActions && (
          <Box sx={{ ml: 2 }}>
            {customActions}
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* User Menu */}
        {showUserMenu && user && (
          <>
            <Tooltip title="User Menu">
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{ ml: 2 }}
              >
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'rgba(255,255,255,0.2)' }}>
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              PaperProps={{
                sx: { minWidth: 250, mt: 1 }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {user.username}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
                <Chip 
                  label={user.role} 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }} 
                />
              </Box>
              <Divider />
              <MenuItem onClick={handleUserMenuClose}>
                <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Dashboard" />
              </MenuItem>
              <MenuItem onClick={handleUserMenuClose}>
                <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Profile" />
              </MenuItem>
              <MenuItem onClick={handleUserMenuClose}>
                <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={onLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default BusinessSuiteNavigation;
